import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import Header from "@/components/Header";
import api from "@/lib/api";
import { toast } from "sonner";

function formatDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
  catch { return iso.slice(0, 16); }
}

export default function Research() {
  const [topic, setTopic] = useState("");
  const [loading, setLoading] = useState(false);
  const [items, setItems] = useState(null);
  const [params] = useSearchParams();
  const linkedSessionId = params.get("session_id");
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/research").then(({ data }) => setItems(data)).catch(() => setItems([]));
  }, []);

  const start = async (e) => {
    e.preventDefault();
    if (!linkedSessionId && topic.trim().length < 3) {
      toast.error("Enter a topic of at least 3 characters.");
      return;
    }
    setLoading(true);
    try {
      const payload = linkedSessionId
        ? { session_id: linkedSessionId, topic: topic.trim() || undefined }
        : { topic: topic.trim() };
      const { data } = await api.post("/research/start", payload);
      navigate(`/research/${data.research_id}`);
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Could not start research.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-forge-900 text-forge-100">
      <Header />
      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
        <div className="text-[10px] tracking-[0.4em] uppercase text-ember-500 mb-4">Research Mentor</div>
        <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-3" data-testid="research-title">
          {linkedSessionId ? "Investigate this reading more deeply." : "What should I investigate?"}
        </h1>
        <p className="font-reading text-lg text-forge-100/70 leading-relaxed max-w-2xl mb-10">
          Research Mentor is an intellectual literacy coach, not a search engine. It helps you map a topic,
          find competing perspectives, identify what evidence is missing, and learn how to evaluate the sources you encounter — without telling you what to think.
        </p>

        <form onSubmit={start} className="mb-14" data-testid="research-form">
          <label className="block text-[10px] uppercase tracking-[0.3em] text-ember-500 mb-3">
            {linkedSessionId ? "Refine the focus (optional)" : "Enter a topic or question"}
          </label>
          <input
            type="text"
            value={topic}
            onChange={(e) => setTopic(e.target.value)}
            placeholder={linkedSessionId ? "Leave blank to investigate the reading's themes" : "e.g. Should AI be used in classrooms?"}
            className="w-full px-4 py-3 bg-forge-800 border border-white/10 rounded-sm text-forge-100 placeholder-forge-100/30 focus:outline-none focus:border-ember-500 transition-colors mb-4"
            data-testid="research-topic-input"
          />
          <button
            type="submit"
            disabled={loading}
            className="px-7 py-3 bg-ember-500 hover:bg-ember-400 disabled:opacity-60 text-forge-900 font-semibold tracking-wide rounded-sm transition-all"
            data-testid="research-start-btn"
          >
            {loading ? "Mapping the landscape…" : "Begin Investigation"}
          </button>
          {loading && (
            <p className="mt-4 text-xs uppercase tracking-[0.25em] text-forge-100/40 animate-pulse">This may take 30–60 seconds.</p>
          )}
        </form>

        <div className="text-[10px] tracking-[0.4em] uppercase text-gold-500 mb-4">Past Investigations</div>
        {items === null ? (
          <p className="text-sm text-forge-100/40">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-sm text-forge-100/40">No prior investigations yet.</p>
        ) : (
          <ul className="divide-y divide-white/5 border-t border-b border-white/5" data-testid="research-list">
            {items.map((r) => (
              <li key={r.research_id} className="py-4 flex justify-between items-center gap-4 flex-wrap">
                <Link to={`/research/${r.research_id}`} className="font-serif text-lg text-forge-100 hover:text-ember-400 transition-colors">{r.topic}</Link>
                <div className="flex items-center gap-4">
                  <span className="text-[10px] uppercase tracking-[0.25em] text-ember-500">{r.mode === "reading_linked" ? "Reading-linked" : "Stand-alone"}</span>
                  <span className="text-[10px] uppercase tracking-[0.25em] text-forge-100/40">{formatDate(r.created_at)}</span>
                </div>
              </li>
            ))}
          </ul>
        )}
      </main>
    </div>
  );
}
